import type { Destination } from "./types";

/**
 * Liste des destinations couvertes par Welconcept.
 *
 * 6 villes : Lomé, Cotonou, Abidjan (Afrique), Paris, Bruxelles (Europe),
 * Dubaï (Moyen-Orient). Pour enrichir : dupliquer un objet et compléter.
 *
 * Coordonnées : [latitude, longitude] en degrés.
 * Format des descriptions : court, factuel — 1 à 3 phrases.
 */
export const destinations: Destination[] = [
  // ─────────────────────────────────────────────────────────
  // AFRIQUE
  // ─────────────────────────────────────────────────────────
  {
    id: "lome",
    city: "Lomé",
    country: { fr: "Togo", en: "Togo" },
    countryCode: "TG",
    region: "Africa",
    coords: [6.1319, 1.2228],
    heroImageSrc: "/cities_activities/diner_plage_togo.jpg",
    intro: {
      fr: "Capitale posée sur le golfe de Guinée, entre plages de cocotiers et effervescence créative — un port d'attache discret pour rayonner en Afrique de l'Ouest.",
      en: "A capital set on the Gulf of Guinea, between coconut-lined beaches and creative energy — a discreet home base for West Africa.",
    },
    hotels: [
      {
        id: "hotel-2-fevrier",
        imageSrc: "/destinations/lome/hotel_2fevrier_lome.png",
        name: "Hôtel 2 Février",
        category: { fr: "Tour emblématique", en: "Iconic tower" },
        description: {
          fr: "La plus haute tour de la ville — suites panoramiques sur l'océan, spa et tables au sommet.",
          en: "The city's tallest tower — panoramic ocean suites, spa and top-floor dining.",
        },
      },
      {
        id: "sarakawa",
        imageSrc: "/destinations/lome/hotel_sarakawa_lome.png",
        name: "Hôtel Sarakawa",
        category: { fr: "Resort en bord de mer", en: "Seafront resort" },
        description: {
          fr: "Parc tropical de 10 hectares face à l'Atlantique, piscine olympique, ambiance club feutrée.",
          en: "Ten hectares of tropical gardens facing the Atlantic, Olympic pool, hushed club feel.",
        },
      },
    ],
    activities: [
      {
        id: "lac-togo",
        imageSrc: "/cities_activities/togoville_visite_togo.jpg",
        name: { fr: "Lac Togo & Togoville en privé", en: "Private Lake Togo & Togoville" },
        category: { fr: "Culture", en: "Culture" },
        description: {
          fr: "Traversée du lac en pirogue privée et visite de la ville historique avec un guide-historien.",
          en: "Private pirogue crossing and a tour of the historic town with a historian guide.",
        },
      },
      {
        id: "diner-plage-lome",
        imageSrc: "/cities_activities/diner_plage_togo.jpg",
        name: { fr: "Dîner pieds dans le sable", en: "Toes-in-the-sand dinner" },
        category: { fr: "Gastronomie", en: "Gastronomy" },
        description: {
          fr: "Table dressée sur une plage privatisée, chef à domicile et brasero au coucher du soleil.",
          en: "A table set on a private beach, personal chef and brazier at sunset.",
        },
      },
    ],
  },
  {
    id: "cotonou",
    city: "Cotonou",
    country: { fr: "Bénin", en: "Benin" },
    countryCode: "BJ",
    region: "Africa",
    coords: [6.3703, 2.3912],
    heroImageSrc: "/cities_activities/ganvie_benin.jpg",
    intro: {
      fr: "Porte d'entrée d'un Bénin en pleine renaissance culturelle — marina neuve, scène artistique vibrante et patrimoine unique au monde à portée de route.",
      en: "Gateway to a Benin in full cultural renaissance — a brand-new marina, a vibrant art scene and one-of-a-kind heritage within easy reach.",
    },
    hotels: [
      {
        id: "sofitel-cotonou",
        imageSrc: "/destinations/cotonou/hotel_sofitel_cotonou.png",
        name: "Sofitel Cotonou Marina Hotel & Spa",
        category: { fr: "Palace de la marina", en: "Marina palace" },
        description: {
          fr: "L'adresse la plus attendue d'Afrique de l'Ouest — front de mer, spa, tables signature.",
          en: "West Africa's most anticipated address — waterfront, spa, signature dining.",
        },
      },
      {
        id: "golden-tulip-diplomate",
        imageSrc: "/destinations/cotonou/hotel_golden_tulip_cotonou.png",
        name: "Golden Tulip Le Diplomate",
        category: { fr: "Hôtel d'affaires", en: "Business hotel" },
        description: {
          fr: "Valeur sûre du quartier des affaires, service constant et piscine dans les jardins.",
          en: "A reliable base in the business district, consistent service and garden pool.",
        },
      },
    ],
    activities: [
      {
        id: "ganvie-pirogue",
        imageSrc: "/cities_activities/ganvie_benin.jpg",
        name: { fr: "Ganvié en pirogue privée", en: "Ganvié by private pirogue" },
        category: { fr: "Découverte", en: "Discovery" },
        description: {
          fr: "La « Venise de l'Afrique » au lever du jour, loin des circuits, avec un guide du village.",
          en: "Africa's 'Venice' at daybreak, far from the crowds, with a village guide.",
        },
      },
      {
        id: "ouidah-histoire",
        imageSrc: "/cities_activities/ouidah_benin.jpg",
        name: { fr: "Ouidah, mémoire & art contemporain", en: "Ouidah, memory & contemporary art" },
        category: { fr: "Culture", en: "Culture" },
        description: {
          fr: "Route des Esclaves, Porte du Non-Retour et collections de la Fondation Zinsou, en journée privée.",
          en: "The Slave Route, the Door of No Return and the Zinsou Foundation's collections, as a private day.",
        },
      },
    ],
  },
  {
    id: "abidjan",
    city: "Abidjan",
    country: { fr: "Côte d'Ivoire", en: "Ivory Coast" },
    countryCode: "CI",
    region: "Africa",
    coords: [5.36, -4.0083],
    heroImageSrc: "/cities_activities/assinie_cote_divoire.jpg",
    intro: {
      fr: "La métropole qui donne le tempo de l'Afrique francophone — lagunes, rooftops, gastronomie et week-ends à Assinie.",
      en: "The metropolis setting the pace of French-speaking Africa — lagoons, rooftops, gastronomy and Assinie weekends.",
    },
    hotels: [
      {
        id: "hotel-ivoire",
        imageSrc: "/destinations/abidjan/hotel_sofitel_abidjan.png",
        name: "Sofitel Abidjan Hôtel Ivoire",
        category: { fr: "Icône moderniste", en: "Modernist icon" },
        description: {
          fr: "Légende de Cocody posée sur la lagune Ébrié — l'adresse des sommets et des grandes soirées.",
          en: "The Cocody legend on the Ébrié lagoon — the address for summits and grand evenings.",
        },
      },
      {
        id: "maison-palmier",
        imageSrc: "/destinations/abidjan/hotel_maison_palmier_abidjan.png",
        name: "La Maison Palmier",
        category: { fr: "Boutique-hôtel", en: "Boutique hotel" },
        description: {
          fr: "Membre Design Hotels — intimité d'une villa, jardin luxuriant, clientèle d'initiés.",
          en: "A Design Hotels member — villa-like intimacy, lush garden, insider crowd.",
        },
      },
    ],
    activities: [
      {
        id: "assinie-lagune",
        imageSrc: "/cities_activities/assinie_cote_divoire.jpg",
        name: { fr: "Week-end privé à Assinie", en: "Private Assinie weekend" },
        category: { fr: "Évasion", en: "Escape" },
        description: {
          fr: "Villa les pieds dans la lagune, bateau privé entre mer et embouchure, chef sur place.",
          en: "A villa on the lagoon, private boat between sea and estuary, chef on site.",
        },
      },
      {
        id: "grand-bassam",
        imageSrc: "/cities_activities/grand_bassam_cote_divoire.jpg",
        name: { fr: "Grand-Bassam avec un historien", en: "Grand-Bassam with a historian" },
        category: { fr: "Culture", en: "Culture" },
        description: {
          fr: "L'ancienne capitale classée UNESCO, ses maisons coloniales et ses ateliers d'artistes.",
          en: "The UNESCO-listed former capital, its colonial houses and artists' studios.",
        },
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // EUROPE
  // ─────────────────────────────────────────────────────────
  {
    id: "paris",
    city: "Paris",
    country: { fr: "France", en: "France" },
    countryCode: "FR",
    region: "Europe",
    coords: [48.8566, 2.3522],
    heroImageSrc: "/cities_activities/croisiere_privee_france.jpg",
    intro: {
      fr: "Le siège historique de Welconcept. Carnet d'adresses cultivé sur plus d'une décennie — palaces, tables, accès culturels privés.",
      en: "Welconcept's historic home. A black book cultivated over more than a decade — palaces, dining, private cultural access.",
    },
    hotels: [
      {
        id: "le-bristol",
        imageSrc: "/destinations/paris/hotel_le_bristol_paris.png",
        name: "Le Bristol Paris",
        category: { fr: "Palace", en: "Palace" },
        description: {
          fr: "Faubourg Saint-Honoré, piscine sur le toit, restaurant Epicure trois étoiles.",
          en: "Faubourg Saint-Honoré, rooftop pool, three-star Epicure restaurant.",
        },
      },
      {
        id: "cheval-blanc",
        imageSrc: "/destinations/paris/hotel_le_cheval_blanc_paris.png",
        name: "Cheval Blanc Paris",
        category: { fr: "Palace contemporain", en: "Contemporary palace" },
        description: {
          fr: "Quai du Louvre, vue Seine, spa Dior, signature LVMH la plus récente à Paris.",
          en: "Quai du Louvre, Seine view, Dior spa, LVMH's most recent Paris signature.",
        },
      },
      {
        id: "ritz-paris",
        imageSrc: "/destinations/paris/hotel_ritz_paris.png",
        name: "Ritz Paris",
        category: { fr: "Palace légendaire", en: "Legendary palace" },
        description: {
          fr: "Place Vendôme — suites historiques, bar Hemingway, l'adresse qui a inventé le mot palace.",
          en: "Place Vendôme — historic suites, Hemingway Bar, the address that invented the word palace.",
        },
      },
    ],
    activities: [
      {
        id: "louvre-private",
        imageSrc: "/cities_activities/visite_louvre_france.webp",
        name: { fr: "Louvre en privatisation", en: "Louvre private opening" },
        category: { fr: "Culture", en: "Culture" },
        description: {
          fr: "Visite en dehors des heures d'ouverture, avec un conservateur du musée.",
          en: "Visit outside opening hours, with a museum curator.",
        },
      },
      {
        id: "michelin-evening",
        imageSrc: "/cities_activities/diner_3_etoiles_france.jpeg",
        name: { fr: "Dîner à quatre mains chez un trois étoiles", en: "Four-hands dinner at a three-star" },
        category: { fr: "Gastronomie", en: "Gastronomy" },
        description: {
          fr: "Menu unique signé par deux chefs étoilés, table en cuisine.",
          en: "Unique menu signed by two starred chefs, table in the kitchen.",
        },
      },
      {
        id: "seine-privee",
        imageSrc: "/cities_activities/croisiere_privee_france.jpg",
        name: { fr: "Croisière privée sur la Seine", en: "Private Seine cruise" },
        category: { fr: "Expérience", en: "Experience" },
        description: {
          fr: "Yacht privatisé au crépuscule, champagne et dîner signé par un chef étoilé à bord.",
          en: "A private yacht at dusk, champagne and dinner by a starred chef on board.",
        },
      },
    ],
  },
  {
    id: "bruxelles",
    city: "Bruxelles",
    country: { fr: "Belgique", en: "Belgium" },
    countryCode: "BE",
    region: "Europe",
    coords: [50.8503, 4.3517],
    heroImageSrc: "/cities_activities/atelier_chocolatier_bruxelles.jpg",
    intro: {
      fr: "Capitale européenne à taille humaine — grandes tables, chocolat d'orfèvre, surréalisme et art de vivre entre Sablon et Châtelain.",
      en: "A European capital on a human scale — great tables, jeweller-grade chocolate, surrealism and art de vivre from Sablon to Châtelain.",
    },
    hotels: [
      {
        id: "hotel-amigo",
        imageSrc: "/destinations/bruxelles/hotel_amigo_bruxelles.png",
        name: "Hôtel Amigo",
        category: { fr: "Palace Rocco Forte", en: "Rocco Forte palace" },
        description: {
          fr: "À un pas de la Grand-Place — élégance flamande, service précis, bar confidentiel.",
          en: "A step from the Grand-Place — Flemish elegance, precise service, confidential bar.",
        },
      },
      {
        id: "juliana-brussels",
        imageSrc: "/destinations/bruxelles/hotel_juliana_bruxelles.png",
        name: "Juliana Hotel Brussels",
        category: { fr: "Boutique-hôtel", en: "Boutique hotel" },
        description: {
          fr: "Écrin feutré près de la place des Martyrs — marbres, velours et piscine sous verrière.",
          en: "A hushed hideaway near Place des Martyrs — marble, velvet and a glass-roofed pool.",
        },
      },
    ],
    activities: [
      {
        id: "atelier-chocolat",
        imageSrc: "/cities_activities/atelier_chocolatier_bruxelles.jpg",
        name: { fr: "Atelier privé chez un maître chocolatier", en: "Private session with a master chocolatier" },
        category: { fr: "Gastronomie", en: "Gastronomy" },
        description: {
          fr: "Création de votre propre assemblage dans un atelier fermé au public, dégustation guidée.",
          en: "Create your own blend in a workshop closed to the public, with a guided tasting.",
        },
      },
      {
        id: "sablon-art",
        imageSrc: "/cities_activities/quartier_sablon_bruxelles.jpg",
        name: { fr: "Sablon & antiquaires avec un expert", en: "Sablon & antique dealers with an expert" },
        category: { fr: "Culture", en: "Culture" },
        description: {
          fr: "Parcours privé chez les galeristes et antiquaires du Sablon, portes ouvertes sur rendez-vous.",
          en: "A private route through Sablon's galleries and antique dealers, doors opened by appointment.",
        },
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // MOYEN-ORIENT
  // ─────────────────────────────────────────────────────────
  {
    id: "dubai",
    city: "Dubaï",
    country: { fr: "Émirats arabes unis", en: "United Arab Emirates" },
    countryCode: "AE",
    region: "Middle East",
    coords: [25.2048, 55.2708],
    heroImageSrc: "/destinations/dubai/hotel_burj_al_arab_dubai.png",
    intro: {
      fr: "Le terrain de jeu de la démesure maîtrisée — palaces posés sur l'eau, désert à vingt minutes, scène gastronomique mondiale.",
      en: "The playground of controlled excess — palaces on the water, desert twenty minutes away, a world-class dining scene.",
    },
    hotels: [
      {
        id: "atlantis-the-royal",
        imageSrc: "/destinations/dubai/hotel_atlantis_the_royal_dubai.png",
        name: "Atlantis The Royal",
        category: { fr: "Palace nouvelle génération", en: "New-generation palace" },
        description: {
          fr: "L'adresse la plus spectaculaire de la Palm — suites avec piscine dans le ciel, tables de chefs mondiaux.",
          en: "The Palm's most spectacular address — sky-pool suites and tables by global chefs.",
        },
      },
      {
        id: "burj-al-arab",
        imageSrc: "/destinations/dubai/hotel_burj_al_arab_dubai.png",
        name: "Burj Al Arab Jumeirah",
        category: { fr: "Icône absolue", en: "Absolute icon" },
        description: {
          fr: "La voile la plus célèbre du monde — suites en duplex, majordome dédié, plage privée.",
          en: "The world's most famous sail — duplex suites, dedicated butler, private beach.",
        },
      },
      {
        id: "oneandonly-the-palm",
        imageSrc: "/destinations/dubai/hotel_one-and-only_the_palm_dubai.png",
        name: "One&Only The Palm",
        category: { fr: "Resort confidentiel", en: "Confidential resort" },
        description: {
          fr: "Le bout de la Palm en version intime — villas avec piscine, plage calme face à la skyline.",
          en: "The tip of the Palm, intimate edition — pool villas and a quiet beach facing the skyline.",
        },
      },
    ],
    activities: [
      {
        id: "desert-prive",
        imageSrc: "/cities_activities/4x4_excursion_emirats_arabes_unis.jpg",
        name: { fr: "Désert en 4x4 privé & dîner sous les étoiles", en: "Private 4x4 desert & dinner under the stars" },
        category: { fr: "Aventure", en: "Adventure" },
        description: {
          fr: "Dunes au coucher du soleil, camp privé, fauconnier et table dressée dans le sable.",
          en: "Sunset dunes, private camp, falconer and a table set in the sand.",
        },
      },
      {
        id: "yacht-marina",
        imageSrc: "/cities_activities/excursion_yacht_emirats_arabes_unis.jpg",
        name: { fr: "Journée yacht autour de la Palm", en: "Yacht day around the Palm" },
        category: { fr: "Mer", en: "Sea" },
        description: {
          fr: "Yacht privé avec équipage, mouillage face à la skyline, chef à bord.",
          en: "Private crewed yacht, anchorage facing the skyline, chef on board.",
        },
      },
      {
        id: "hydravion-palm",
        imageSrc: "/cities_activities/hydravion_emirats_arabes_unis.jpg",
        name: { fr: "Hydravion au-dessus de la Palm", en: "Seaplane over the Palm" },
        category: { fr: "Aventure", en: "Adventure" },
        description: {
          fr: "Décollage sur l'eau et survol privé de la Palm, du World et de la skyline au couchant.",
          en: "Water take-off and a private flight over the Palm, the World and the skyline at sunset.",
        },
      },
    ],
  },
];

/**
 * Helper : retourne les destinations regroupées par région, dans l'ordre voulu
 * pour l'affichage (Afrique en premier — c'est le focus de Welconcept).
 */
export const DESTINATIONS_BY_REGION_ORDER: Destination["region"][] = [
  "Africa",
  "Europe",
  "Middle East",
  "Asia",
  "Americas",
];
